import { toast, ToastOptions, ToastContent, Id } from 'react-toastify'
import { useNavigate, NavigateOptions } from 'react-router-dom'
import { useCallback, useMemo } from 'react'

interface ToastifyOptions extends Omit<ToastOptions, 'onClose'> {
  /**
   * Optional navigation to perform after showing the toast
   * Can be a path string, number (for navigate(-1)), or null to skip navigation
   */
  navigateTo?: string | number | null
  /**
   * Optional navigation options (e.g., { replace: true })
   */
  navigateOptions?: NavigateOptions
  /**
   * Callback to execute when toast closes
   */
  onClose?: () => void
  /**
   * Duration in milliseconds before the toast auto-closes.
   * Set to `false` to disable auto-close.
   */
  duration?: number | false
}

/**
 * Custom hook that wraps react-toastify with safe navigation
 *
 * Uses react-toastify which handles DOM portal management properly,
 * avoiding the removeChild race condition that occurred with Chakra UI toast.
 *
 * @returns Toast functions (success, error, info, warning, default)
 *
 * @example
 * ```tsx
 * const toast = useToastify()
 *
 * // Success toast with navigation
 * toast.success('User created successfully!', {
 *   navigateTo: '/users'
 * })
 *
 * // Error toast without navigation
 * toast.error('Failed to create user')
 *
 * // Go back after success
 * toast.success('Updated!', {
 *   navigateTo: -1
 * })
 * ```
 */
export const useToastify = () => {
  const navigate = useNavigate()

  const showToast = useCallback(
    (type: 'success' | 'error' | 'info' | 'warning' | 'default', content: ToastContent, options?: ToastifyOptions): Id => {
      const { navigateTo, navigateOptions, onClose, duration, ...toastOptions } = options || {}

      // Navigate first if requested
      if (navigateTo !== undefined && navigateTo !== null) {
        if (typeof navigateTo === 'number') {
          navigate(navigateTo)
        } else {
          navigate(navigateTo, navigateOptions)
        }
      }

      // Then show the toast (will be visible on the new page)
      const toastFn = type === 'default' ? toast : toast[type]
      const toastId = toastFn(content, {
        ...toastOptions,
        autoClose: duration ?? toastOptions.autoClose,
        onClose: () => {
          // Execute custom onClose if provided
          if (onClose) {
            onClose()
          }
        }
      })

      return toastId
    },
    [navigate]
  )

  return useMemo(
    () => ({
      success: (content: ToastContent, options?: ToastifyOptions) => showToast('success', content, options),
      error: (content: ToastContent, options?: ToastifyOptions) => showToast('error', content, options),
      info: (content: ToastContent, options?: ToastifyOptions) => showToast('info', content, options),
      warning: (content: ToastContent, options?: ToastifyOptions) => showToast('warning', content, options),
      default: (content: ToastContent, options?: ToastifyOptions) => showToast('default', content, options)
    }),
    [showToast]
  )
}
